import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Avatar, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, List, ListItem, ListItemAvatar, ListItemText,
  MenuItem, Paper, Select, TextField, Typography, makeStyles
} from "@material-ui/core";
import { Add, Close, Reply, Send } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import VoiceRooms from "./VoiceRooms";

const useStyles = makeStyles(theme => ({
  root: { display: "flex", height: "calc(100vh - 48px)", overflow: "hidden" },
  sidebar: { width: 320, borderRight: `1px solid ${theme.palette.divider}`, display: "flex", flexDirection: "column" },
  sidebarHeader: { padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between" },
  chatList: { overflowY: "auto", flex: 1 },
  unread: { background: theme.palette.primary.main, color: "#fff", borderRadius: 12, padding: "2px 8px", fontSize: 12 },
  conversation: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
  header: { padding: "10px 16px", borderBottom: `1px solid ${theme.palette.divider}` },
  messages: { flex: 1, overflowY: "auto", padding: 16, background: theme.palette.background.default },
  bubbleRow: { display: "flex", marginBottom: 8 },
  ownRow: { justifyContent: "flex-end" },
  bubble: { maxWidth: "70%", padding: "8px 12px", borderRadius: 10, background: theme.palette.background.paper, boxShadow: theme.shadows[1] },
  ownBubble: { background: theme.palette.primary.main, color: theme.palette.primary.contrastText },
  sender: { fontWeight: 700, fontSize: 12 },
  messageMeta: { fontSize: 10, opacity: 0.7, textAlign: "right" },
  replyPreview: { borderLeft: "3px solid currentColor", paddingLeft: 7, marginBottom: 5, opacity: 0.8, fontSize: 12 },
  composer: { padding: 10, display: "flex", gap: 8, alignItems: "flex-end", borderTop: `1px solid ${theme.palette.divider}` },
  composerInput: { flex: 1 },
  empty: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center" },
  selected: { background: `${theme.palette.action.selected} !important` }
}));

const InternalChat = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [chatType, setChatType] = useState("direct");
  const [chatName, setChatName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const bottomRef = useRef();

  const chatTitle = chat => chat?.type === "group" ? chat.name : chat?.participants?.find(item => Number(item.id) !== Number(user.id))?.name || "Conversa";
  const loadChats = async () => { try { setChats((await api.get("/internal-chats")).data); } catch (err) { toastError(err); } };
  const openChat = async chat => {
    try {
      setSelected(chat); setMessages((await api.get(`/internal-chats/${chat.id}/messages`)).data);
      await api.put(`/internal-chats/${chat.id}/read`); setChats(current => current.map(item => item.id === chat.id ? { ...item, unread: 0 } : item));
    } catch (err) { toastError(err); }
  };

  useEffect(() => {
    Promise.all([loadChats(), api.get("/users")]).then(([, result]) => setUsers(result.data.users.filter(item => Number(item.id) !== Number(user.id)))).catch(toastError);
  }, []);
  useEffect(() => {
    if (!selected) return undefined;
    const socket = openSocket(); socket.emit("joinInternalChat", selected.id);
    socket.on("internalChatMessage", message => {
      setMessages(current => current.some(item => item.id === message.id) ? current : [...current, message]);
      api.put(`/internal-chats/${selected.id}/read`).catch(() => {});
    });
    return () => { socket.emit("leaveInternalChat", selected.id); socket.disconnect(); };
  }, [selected?.id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const createChat = async () => {
    try {
      const { data } = await api.post("/internal-chats", { type: chatType, name: chatType === "group" ? chatName : null, userIds: selectedUsers });
      setCreateOpen(false); setChatName(""); setSelectedUsers([]); await loadChats(); openChat(data);
    } catch (err) { toastError(err); }
  };
  const send = async () => {
    if (!body.trim() || !selected) return;
    const text = body; setBody("");
    try { await api.post(`/internal-chats/${selected.id}/messages`, { body: text, replyToId: replyTo?.id || null }); setReplyTo(null); }
    catch (err) { setBody(text); toastError(err); }
  };

  return <MainContainer><div className={classes.root}>
    <Paper square className={classes.sidebar}>
      <div className={classes.sidebarHeader}><Typography variant="h6">Chat interno</Typography><IconButton color="primary" onClick={() => setCreateOpen(true)}><Add /></IconButton></div>
      <VoiceRooms />
      <List className={classes.chatList}>{chats.map(chat => <ListItem button key={chat.id} selected={selected?.id === chat.id} classes={{ selected: classes.selected }} onClick={() => openChat(chat)}>
        <ListItemAvatar><Avatar>{chatTitle(chat).slice(0, 1).toUpperCase()}</Avatar></ListItemAvatar>
        <ListItemText primary={chatTitle(chat)} secondary={chat.lastMessage?.body || "Nova conversa"} />
        {chat.unread > 0 && <span className={classes.unread}>{chat.unread}</span>}
      </ListItem>)}</List>
    </Paper>
    {selected ? <div className={classes.conversation}>
      <div className={classes.header}><Typography variant="h6">{chatTitle(selected)}</Typography><Typography variant="caption">{selected.type === "group" ? `${selected.participants?.length || 0} participantes` : "Conversa direta"}</Typography></div>
      <div className={classes.messages}>{messages.map(message => {
        const own = Number(message.senderId) === Number(user.id);
        const replied = message.replyToId ? messages.find(item => item.id === message.replyToId) : null;
        return <div key={message.id} className={`${classes.bubbleRow} ${own ? classes.ownRow : ""}`}><div className={`${classes.bubble} ${own ? classes.ownBubble : ""}`}>
          {!own && <div className={classes.sender}>{message.sender?.name}</div>}
          {replied && <div className={classes.replyPreview}>{replied.sender?.name}: {replied.body}</div>}
          <div>{message.deletedAt ? "Mensagem removida" : message.body}</div>
          <div className={classes.messageMeta}>{new Date(message.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}<IconButton size="small" color="inherit" onClick={() => setReplyTo(message)}><Reply fontSize="small" /></IconButton></div>
        </div></div>;
      })}<div ref={bottomRef} /></div>
      {replyTo && <div className={classes.replyPreview} style={{ margin: "8px 12px 0" }}>Respondendo a {replyTo.sender?.name}: {replyTo.body}<IconButton size="small" onClick={() => setReplyTo(null)}><Close /></IconButton></div>}
      <div className={classes.composer}><TextField className={classes.composerInput} multiline maxRows={4} variant="outlined" size="small" placeholder="Digite uma mensagem" value={body} onChange={e => setBody(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} /><IconButton color="primary" onClick={send}><Send /></IconButton></div>
    </div> : <div className={classes.empty}><Typography color="textSecondary">Selecione ou crie uma conversa</Typography></div>}
    <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth><DialogTitle>Nova conversa</DialogTitle><DialogContent dividers>
      <FormControl fullWidth margin="dense"><InputLabel>Tipo</InputLabel><Select value={chatType} onChange={e => { setChatType(e.target.value); setSelectedUsers([]); }}><MenuItem value="direct">Conversa direta</MenuItem><MenuItem value="group">Grupo</MenuItem></Select></FormControl>
      {chatType === "group" && <TextField fullWidth margin="dense" label="Nome do grupo" value={chatName} onChange={e => setChatName(e.target.value)} />}
      <FormControl fullWidth margin="dense"><InputLabel>Participantes</InputLabel><Select multiple={chatType === "group"} value={chatType === "group" ? selectedUsers : selectedUsers[0] || ""} onChange={e => setSelectedUsers(chatType === "group" ? e.target.value : [e.target.value])} renderValue={value => { const ids = Array.isArray(value) ? value : [value]; return users.filter(item => ids.includes(item.id)).map(item => item.name).join(", "); }}>{users.map(item => <MenuItem key={item.id} value={item.id}>{chatType === "group" && <Checkbox checked={selectedUsers.includes(item.id)} />}{item.name}</MenuItem>)}</Select></FormControl>
    </DialogContent><DialogActions><Button onClick={() => setCreateOpen(false)}>Cancelar</Button><Button color="primary" variant="contained" disabled={selectedUsers.length === 0 || (chatType === "group" && !chatName.trim())} onClick={createChat}>Criar</Button></DialogActions></Dialog>
  </div></MainContainer>;
};
export default InternalChat;
